'use client'

import { useState } from 'react'
import { Bell, X } from 'lucide-react'
import { subscribeToPush, savePushSubscription } from '@/lib/notifications/push-subscription'

interface PushPermissionModalProps {
  isOpen: boolean
  onClose: () => void
  onEnabled: () => void
}

const DISMISSED_KEY = 'pv-push-prompt-dismissed'

export function PushPermissionModal({ isOpen, onClose, onEnabled }: PushPermissionModalProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'denied'>('idle')

  if (!isOpen) return null

  const handleEnable = async () => {
    setStatus('loading')
    const subscription = await subscribeToPush()
    if (subscription) {
      await savePushSubscription(subscription)
      onEnabled()
      onClose()
    } else {
      // Browser denied or unsupported
      setStatus('denied')
    }
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, '1')
    } catch { /* localStorage unavailable */ }
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDismiss} />

      {/* Modal */}
      <div
        className="relative w-full max-w-sm rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-6 shadow-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="push-modal-heading"
      >
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-brand-indigo)]/10">
          <Bell className="h-6 w-6 text-[var(--color-brand-indigo)]" />
        </div>

        <h2
          id="push-modal-heading"
          className="text-lg font-bold text-[var(--color-text-primary)]"
          style={{ fontFamily: 'var(--font-heading, "Plus Jakarta Sans", sans-serif)' }}
        >
          Stay in the loop
        </h2>
        <p
          className="mt-1.5 text-sm text-[var(--color-text-secondary)]"
          style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
        >
          Get notified when someone stars your prompt, follows you, or when a job matches your search.
        </p>

        {status === 'denied' && (
          <p className="mt-3 rounded-lg bg-[var(--color-status-warning)]/10 px-3 py-2 text-xs text-[var(--color-text-secondary)]">
            Push notifications were blocked. You can enable them in your browser settings.
          </p>
        )}

        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={handleEnable}
            disabled={status === 'loading'}
            className="flex-1 rounded-lg bg-[var(--color-brand-indigo)] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {status === 'loading' ? 'Enabling...' : 'Enable notifications'}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="rounded-lg border border-[var(--color-border-default)] px-4 py-2.5 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)]"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  )
}

/** Check if the user has previously dismissed the push prompt. */
export function wasPushPromptDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}
